import { useMemo, useState } from "react";
import RfidNav from "./RfidNav";
import {
  cardStyle,
  generateEpc,
  getAssociations,
  getRfidConfig,
  labelStyle,
  saveAssociations,
  useWarehouseProducts,
  useWarehouses,
} from "./rfidCommon";

// Association des étiquettes RFID (EPC) aux produits du système.
// Les associations sont conservées dans ce navigateur en attendant le backend.

const EPC_REGEX = /^[0-9A-F]{8,32}$/;

const RfidAssociation = () => {
  const config = useMemo(() => getRfidConfig(), []);
  const { warehouses, loading: loadingWarehouses } = useWarehouses();
  const [warehouseId, setWarehouseId] = useState("");
  const { products, loading: loadingProducts } =
    useWarehouseProducts(warehouseId);

  const [associations, setAssociations] = useState(getAssociations());
  const [productSearch, setProductSearch] = useState("");
  const [selected, setSelected] = useState(null);
  const [epcInput, setEpcInput] = useState("");
  const [batchCount, setBatchCount] = useState(10);
  const [message, setMessage] = useState(null); // { type, text }
  const [tableSearch, setTableSearch] = useState("");

  const persist = (list) => {
    setAssociations(list);
    saveAssociations(list);
  };

  const filteredProducts = products.filter((p) => {
    const text = `${p.code} ${p.barcode} ${p.name}`.toLowerCase();
    return text.includes(productSearch.trim().toLowerCase());
  });

  const countFor = (code) =>
    associations.filter((a) => a.code === code).length;

  const epcExists = (epc) => associations.some((a) => a.epc === epc);

  const makeAssociation = (epc) => ({
    epc,
    code: selected.code,
    barcode: selected.barcode,
    name: selected.name,
    createdAt: new Date().toISOString(),
  });

  const onAddEpc = () => {
    const epc = epcInput.trim().toUpperCase();
    if (!selected) return setMessage({ type: "danger", text: "Choisissez d'abord un produit" });
    if (!EPC_REGEX.test(epc))
      return setMessage({
        type: "danger",
        text: "EPC invalide : 8 à 32 caractères hexadécimaux (0-9, A-F)",
      });
    if (epcExists(epc))
      return setMessage({ type: "danger", text: "Cette étiquette est déjà associée" });
    persist([makeAssociation(epc), ...associations]);
    setEpcInput("");
    setMessage({ type: "success", text: `Étiquette associée à ${selected.name}` });
  };

  const onEncodeBatch = () => {
    if (!selected) return setMessage({ type: "danger", text: "Choisissez d'abord un produit" });
    const count = Math.min(Math.max(parseInt(batchCount, 10) || 0, 1), 500);
    const existing = new Set(associations.map((a) => a.epc));
    const created = [];
    while (created.length < count) {
      const epc = generateEpc(config.epcFormat);
      if (!existing.has(epc)) {
        existing.add(epc);
        created.push(makeAssociation(epc));
      }
    }
    persist([...created, ...associations]);
    setMessage({
      type: "success",
      text: `${count} étiquettes encodées pour ${selected.name}`,
    });
  };

  const onDelete = (epc) => persist(associations.filter((a) => a.epc !== epc));

  const onDeleteAll = () => {
    if (window.confirm("Supprimer toutes les associations d'étiquettes ?")) {
      persist([]);
      setMessage(null);
    }
  };

  const visibleAssociations = associations.filter((a) =>
    `${a.epc} ${a.code} ${a.barcode} ${a.name}`
      .toLowerCase()
      .includes(tableSearch.trim().toLowerCase())
  );

  return (
    <div className="content-wrapper" style={{ marginTop: "0" }}>
      <section className="content-header">
        <div className="container-fluid">
          <div className="mb-3">
            <h4 className="mb-1">
              <i className="fas fa-tags mr-2"></i>Association des étiquettes
            </h4>
            <small className="text-muted">
              Associez chaque étiquette RFID (EPC) à un produit de votre catalogue
            </small>
          </div>
          <RfidNav />
        </div>
      </section>

      <section className="content">
        <div className="container-fluid">
          <div className="row">
            <div className="col-lg-6">
              <div className="card card-outline card-dark" style={cardStyle}>
                <div className="card-header">
                  <h3 className="card-title">
                    <i className="fas fa-search mr-2"></i>1. Choisir un produit
                  </h3>
                </div>
                <div className="card-body">
                  <div className="form-group">
                    <label style={labelStyle}>Dépôt</label>
                    <select
                      className="form-control"
                      value={warehouseId}
                      onChange={(e) => {
                        setWarehouseId(e.target.value);
                        setSelected(null);
                      }}
                      disabled={loadingWarehouses}
                    >
                      <option value="">
                        {loadingWarehouses ? "Chargement..." : "Choisir..."}
                      </option>
                      {warehouses.map((w) => (
                        <option key={w.id} value={w.id}>
                          {w.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  <input
                    type="text"
                    className="form-control mb-2"
                    placeholder="Chercher par code, code à barre ou libellé"
                    value={productSearch}
                    onChange={(e) => setProductSearch(e.target.value)}
                    disabled={products.length === 0}
                  />
                </div>
                <div
                  className="table-responsive"
                  style={{ maxHeight: 340, overflowY: "auto" }}
                >
                  <table className="table table-hover mb-0">
                    <thead className="thead-light">
                      <tr>
                        <th>Code</th>
                        <th>Libellé</th>
                        <th className="text-center">Étiquettes</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredProducts.length === 0 && (
                        <tr>
                          <td colSpan={3} className="text-center text-muted py-4">
                            {loadingProducts
                              ? "Chargement..."
                              : warehouseId === ""
                              ? "Choisissez un dépôt"
                              : "Aucun produit"}
                          </td>
                        </tr>
                      )}
                      {filteredProducts.map((p) => (
                        <tr
                          key={p.id}
                          onClick={() => setSelected(p)}
                          style={{
                            cursor: "pointer",
                            backgroundColor:
                              selected && selected.id === p.id
                                ? "#e9ecef"
                                : undefined,
                          }}
                        >
                          <td>
                            <span className="badge badge-light border">
                              {p.code}
                            </span>
                          </td>
                          <td>
                            <strong>{p.name}</strong>
                            <small className="text-muted d-block">
                              {p.barcode}
                            </small>
                          </td>
                          <td className="text-center">
                            <span className="badge badge-dark">
                              {countFor(p.code)}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

            <div className="col-lg-6">
              <div className="card card-outline card-dark" style={cardStyle}>
                <div className="card-header">
                  <h3 className="card-title">
                    <i className="fas fa-tag mr-2"></i>2. Associer des étiquettes
                  </h3>
                </div>
                <div className="card-body">
                  {selected ? (
                    <div className="mb-3 p-2 rounded" style={{ background: "#f8f9fa", border: "1px solid #e9ecef" }}>
                      <strong>{selected.name}</strong>
                      <small className="text-muted d-block">
                        {selected.code} · {selected.barcode}
                      </small>
                    </div>
                  ) : (
                    <div className="text-muted mb-3">
                      Sélectionnez un produit dans la liste
                    </div>
                  )}

                  <div className="form-group">
                    <label style={labelStyle}>Scanner ou saisir un EPC</label>
                    <div className="input-group">
                      <input
                        type="text"
                        className="form-control"
                        style={{ fontFamily: "monospace" }}
                        placeholder="Passez l'étiquette devant le lecteur"
                        value={epcInput}
                        onChange={(e) => setEpcInput(e.target.value)}
                        onKeyDown={(e) => e.key === "Enter" && onAddEpc()}
                      />
                      <div className="input-group-append">
                        <button className="btn btn-dark" onClick={onAddEpc}>
                          <i className="fas fa-link mr-1"></i>
                          Associer
                        </button>
                      </div>
                    </div>
                    <small className="text-muted">
                      Un lecteur en mode clavier saisit l'EPC puis valide avec Entrée.
                    </small>
                  </div>

                  <div className="form-group mb-0">
                    <label style={labelStyle}>Encoder un lot d'étiquettes</label>
                    <div className="input-group">
                      <input
                        type="number"
                        min="1"
                        max="500"
                        className="form-control"
                        value={batchCount}
                        onChange={(e) => setBatchCount(e.target.value)}
                      />
                      <div className="input-group-append">
                        <button className="btn btn-outline-dark" onClick={onEncodeBatch}>
                          <i className="fas fa-magic mr-1"></i>
                          Générer et associer
                        </button>
                      </div>
                    </div>
                  </div>

                  {message && (
                    <div className={`alert alert-${message.type} mt-3 mb-0 py-2`}>
                      {message.text}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          <div className="card card-outline card-dark" style={cardStyle}>
            <div className="card-header">
              <div className="d-flex flex-wrap justify-content-between align-items-center">
                <h3 className="card-title mb-0">
                  <i className="fas fa-list mr-2"></i>Étiquettes associées
                  <span className="badge badge-dark ml-2">
                    {visibleAssociations.length}
                  </span>
                </h3>
                <div className="d-flex mt-2 mt-sm-0">
                  <input
                    type="text"
                    className="form-control form-control-sm mr-2"
                    style={{ width: 220 }}
                    placeholder="Chercher"
                    value={tableSearch}
                    onChange={(e) => setTableSearch(e.target.value)}
                  />
                  <button
                    className="btn btn-sm btn-outline-danger"
                    onClick={onDeleteAll}
                    disabled={associations.length === 0}
                  >
                    <i className="fas fa-trash mr-1"></i>
                    Tout supprimer
                  </button>
                </div>
              </div>
            </div>
            <div
              className="card-body p-0 table-responsive"
              style={{ maxHeight: 420, overflowY: "auto" }}
            >
              <table className="table table-hover table-striped mb-0">
                <thead className="thead-light">
                  <tr>
                    <th>EPC</th>
                    <th>Code</th>
                    <th>Libellé</th>
                    <th>Date</th>
                    <th className="text-center">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {visibleAssociations.length === 0 && (
                    <tr>
                      <td colSpan={5} className="text-center text-muted py-4">
                        <i className="fas fa-tags fa-2x mb-2 d-block"></i>
                        Aucune étiquette associée
                      </td>
                    </tr>
                  )}
                  {visibleAssociations.slice(0, 200).map((a) => (
                    <tr key={a.epc}>
                      <td style={{ fontFamily: "monospace", fontSize: 13 }}>
                        {a.epc}
                      </td>
                      <td>
                        <span className="badge badge-light border">{a.code}</span>
                      </td>
                      <td>
                        <strong>{a.name}</strong>
                      </td>
                      <td>{new Date(a.createdAt).toLocaleString("fr-FR")}</td>
                      <td className="text-center">
                        <button
                          className="btn btn-sm btn-outline-danger"
                          title="Supprimer"
                          onClick={() => onDelete(a.epc)}
                        >
                          <i className="fas fa-trash"></i>
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {visibleAssociations.length > 200 && (
              <div className="card-footer text-muted text-center">
                200 premières étiquettes affichées sur {visibleAssociations.length}
              </div>
            )}
          </div>
        </div>
      </section>
    </div>
  );
};

export default RfidAssociation;
